import { Injectable } from "@angular/core";
import { CoreHttpClient } from "@obg/ngx.core";
import {
	ceil,
	forOwn,
	head,
	inRange,
	isNumber,
	isUndefined,
	filter as lodashFilter,
	map as lodashMap,
} from "lodash-es";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { DepositDetails } from "../payment/payment.model";

import { RewardConfigService } from "./reward.config";
import {
	Reward,
	RewardFilter,
	RewardLockupType,
	RewardPayoutStrategyType,
	RewardResponse,
	Rewards,
	RewardsResponse,
	RewardStateFilterType,
	RewardStatePayload,
	RewardStateType,
	RewardUpdatePayload,
	RewardUpdateRequest,
} from "./reward.model";

/**
 * @deprecated [fabric-deprecated] - Use fabric RewardService from app-account instead
 */
@Injectable({
	providedIn: "root",
})
export class RewardService {
	constructor(
		private httpClient: CoreHttpClient,
		private config: RewardConfigService
	) {}

	getAllByStates(stateFilters?: RewardStateFilterType[]): Observable<Rewards> {
		return this.httpClient
			.get<RewardsResponse>({
				url: this.config.getRewardsEndpoint(),
				searchParams: stateFilters ? { state: stateFilters.join(",") } : {},
			})
			.pipe(map((rewardsResponse) => this.processRawRewardsResponse(rewardsResponse)));
	}

	get(filter: RewardFilter): Observable<Reward> {
		return this.httpClient
			.get<RewardResponse>({
				url: this.config.getRewardEndpoint(),
				searchParams: {
					type: filter.objectType,
					identifier: filter.identifier,
				},
			})
			.pipe(map((reward) => this.processRawReward(reward)));
	}

	forfeit(reward: Reward): Observable<object> {
		const rewardUpdateRequest: RewardUpdateRequest = {
			state: RewardStatePayload.Ended,
		};

		return this.update(reward, rewardUpdateRequest);
	}

	claim(rewardUpdatePayload: RewardUpdatePayload): Observable<object> {
		const rewardUpdateRequest: RewardUpdateRequest = {
			state: RewardStatePayload.Active,
			data: {
				amount: rewardUpdatePayload.claimAmount,
				currencyCode: rewardUpdatePayload.currencyCode,
				bonusCode: rewardUpdatePayload.bonusCode,
			},
		};

		return this.update(rewardUpdatePayload.reward, rewardUpdateRequest);
	}

	/**
	 * converts wagered amount to claimable amount by using the depositReturnPercentage as a conversion ratio
	 * if number is not whole, rounds UP to closest two decimal places
	 */
	minClaimAmountToMinLockAmount(claimAmount: number, percentage: number): number {
		return ceil((claimAmount * 100) / percentage, 2);
	}

	isClaimable(reward: Reward, depositDetails: DepositDetails): boolean {
		if (
			!isNumber(depositDetails.amount) ||
			!isNumber(reward.minClaimAmount) ||
			!isNumber(reward.depositReturnPercentage)
		) {
			return false;
		}

		return !inRange(
			depositDetails.amount,
			this.minClaimAmountToMinLockAmount(reward.minClaimAmount, reward.depositReturnPercentage)
		);
	}

	private update(reward: Reward, rewardUpdateRequest: RewardUpdateRequest): Observable<object> {
		return this.httpClient.patch({
			url: this.config.getRewardsUpdateEndpoint(),
			data: rewardUpdateRequest,
			searchParams: {
				type: reward.objectType,
				identifier: reward.identifier,
			},
		});
	}

	private getStateFilter(stateType: RewardStateType) {
		switch (stateType) {
			case RewardStateType.Active:
				return RewardStateFilterType.Active;
			case RewardStateType.PendingPayout:
				return RewardStateFilterType.Pending;
			case RewardStateType.Completed:
			case RewardStateType.Forfeited:
			case RewardStateType.Expired:
			case RewardStateType.EndBalanceReached:
				return RewardStateFilterType.Ended;
			default:
				return RewardStateFilterType.Claimable;
		}
	}

	private processRawReward(rawReward: RewardResponse): Reward {
		const textContentItems = rawReward.content.textContentItems;
		const imageContentItems = rawReward.content.imageContentItems;
		// legacy bonus defines this as percentage, current model uses percent
		const depositRewardPercentage = isUndefined(rawReward.percentage) ? rawReward.percent : rawReward.percentage;
		const state = rawReward.state;

		return {
			bonusContext: rawReward.bonusContexts ?? [],
			objectType: rawReward.objectType,
			image: imageContentItems && head(imageContentItems.image),
			title: textContentItems ? unescape(textContentItems.title) : undefined,
			description: textContentItems ? unescape(textContentItems.description) : undefined,
			termsAndConditions: textContentItems ? unescape(textContentItems.termsAndConditions) : undefined,
			identifier: rawReward.identifier,
			state,
			stateFilter: this.getStateFilter(state),
			currencyCode: rawReward.currencyCode,
			initialBonusAmount: this.getInitialBonusAmount(rawReward),
			currentBonusAmount: this.getCurrentBonusAmount(rawReward),
			currentWageringRequirement: rawReward.currentWageringRequirement,
			isRealMoneyBonus: rawReward.isRealMoneyBonus,
			claimedDate: rawReward.claimedDate,
			expiryDate: rawReward.expires,
			claimableUntilDate: rawReward.claimableTo,
			forfeitedDate: rawReward.forfeitedDate,
			payoutStrategy: rawReward.payoutStrategy,
			depositReturnPercentage: depositRewardPercentage,
			minClaimAmount: this.getMinClaimAmount(rawReward),
			maxClaimAmount: this.getMaxClaimAmount(rawReward),
			maxAmountToWager: rawReward.initialWageringRequirement,
			amountWagered: this.getAmountWagered(rawReward),
			hasWageringProgress: rawReward.hasWageringRequirement,
			percentageComplete: this.getPercentageComplete(rawReward),
			isLockUpBonus: this.checkIfLockUpBonus(rawReward),
			rewardCategory: rawReward.rewardCategory,
			groupedBonusContexts: rawReward.groupedBonusContexts,
		};
	}

	private getAmountWagered(rawReward: RewardResponse): number | undefined {
		if (isNumber(rawReward.initialWageringRequirement) && isNumber(rawReward.currentWageringRequirement)) {
			return rawReward.initialWageringRequirement - rawReward.currentWageringRequirement;
		}

		return undefined;
	}

	/** min claim amount is actually a minimum amount user must wager. it is converted here to a usable min claim amount */
	private getMinClaimAmount(rawReward: RewardResponse): number | undefined {
		const depositRewardPercentage = isUndefined(rawReward.percentage) ? rawReward.percent : rawReward.percentage;

		if (isNumber(rawReward.minClaimAmount) && depositRewardPercentage) {
			return this.wageredAmountToClaimAmount(rawReward.minClaimAmount, depositRewardPercentage);
		}

		if (isNumber(rawReward.minWageredAmount) && depositRewardPercentage) {
			return this.wageredAmountToClaimAmount(rawReward.minWageredAmount, depositRewardPercentage);
		}

		return undefined;
	}

	/** legacy lock up uses maxRecievedAmount (typo from campaigns), current uses maxClaimAmount */
	private getMaxClaimAmount(rawReward: RewardResponse): number | undefined {
		if (rawReward.maxRecievedAmount) {
			return Math.floor(rawReward.maxRecievedAmount);
		}
		if (rawReward.maxClaimAmount) {
			return Math.floor(rawReward.maxClaimAmount);
		}
		return undefined;
	}

	/** converts wagered amount to claimable amount by using the depositReturnPercentage as a conversion ratio
	 * if number is not whole, rounds UP to closest two decimal places
	 */
	private wageredAmountToClaimAmount(wageredAmount: number, depositReturnPercentage: number) {
		return ceil(wageredAmount * (depositReturnPercentage / 100), 2);
	}

	/** percentage complete = how much has been wagered, divided by original amount (wageredAmount * 100) / originalAmount */
	private getPercentageComplete(rawReward: RewardResponse): number | undefined {
		if (isNumber(rawReward.initialWageringRequirement) && isNumber(rawReward.currentWageringRequirement)) {
			return Math.floor(
				((rawReward.initialWageringRequirement - rawReward.currentWageringRequirement) * 100) /
					rawReward.initialWageringRequirement
			);
		}

		return undefined;
	}

	/** reason for this is because a Legacy bonus (Bonus 2.0 tool) has a different signature */
	private getInitialBonusAmount(rawReward: RewardResponse): number | undefined {
		if (isNumber(rawReward.initialBonusAmount)) {
			return rawReward.initialBonusAmount;
		}

		if (isNumber(rawReward.initialBonusMoney)) {
			return rawReward.initialBonusMoney;
		}

		return undefined;
	}

	/** reason for this is because a Legacy bonus (Bonus 2.0 tool) has a different signature */
	private getCurrentBonusAmount(rawReward: RewardResponse): number | undefined {
		if (isNumber(rawReward.currentBonusAmount)) {
			return rawReward.currentBonusAmount;
		}

		if (isNumber(rawReward.currentBonusMoney)) {
			return rawReward.currentBonusMoney;
		}

		return undefined;
	}

	/** discerns whether it is a deposit or a lock-up bonus - which are very similar */
	private checkIfLockUpBonus(rawReward: RewardResponse): boolean {
		const payoutStrategy =
			rawReward.payoutStrategy && rawReward.payoutStrategy.strategy
				? rawReward.payoutStrategy.strategy.toLowerCase()
				: null;

		// if lockup bonus type, it can be either deposit or lock-up, only visible difference in model is payout strategy
		// legacy lock up bonuses are all lock-up and never deposit
		const checkIfLockUpBonus =
			(rawReward.objectType &&
				rawReward.objectType === RewardLockupType.LockUpDepositBonusType &&
				payoutStrategy === RewardPayoutStrategyType.LockUpPayoutStrategy.toLowerCase()) ||
			rawReward.objectType === RewardLockupType.LockUpLegacyBonusType;

		return checkIfLockUpBonus;
	}

	private processRawRewardsResponse(rewardsResponse: RewardsResponse): Rewards {
		const rewards: Rewards = {};
		const filteredRewardsResponse = this.filterRewardsWithoutContent(rewardsResponse);

		forOwn(filteredRewardsResponse, (rewardsList, rewardsState) => {
			rewards[rewardsState] = lodashMap(rewardsList, (reward) => this.processRawReward(reward));
		});
		return rewards;
	}

	private filterRewardsWithoutContent(rewardResponse: RewardsResponse): RewardsResponse {
		const updatedResponse: RewardsResponse = {};
		forOwn(rewardResponse, (rewardsList, key) => {
			updatedResponse[key] = lodashFilter(
				rewardsList,
				(reward) =>
					!!(
						reward.content.textContentItems &&
						reward.content.textContentItems.description &&
						reward.content.textContentItems.title &&
						reward.content.textContentItems.termsAndConditions
					)
			);
		});
		return updatedResponse;
	}
}
